/**
 * POS RFID Analytics Page
 * =======================
 * 
 * Store journey analytics with zone traffic, dwell time, and conversion metrics.
 */

import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Radio,
  ArrowLeft,
  MapPin,
  Clock,
  Users,
  TrendingUp,
  AlertTriangle,
  Lightbulb,
  Plus,
  Edit2,
  Trash2,
  BarChart3,
  Target,
  Activity,
} from "lucide-react";
import { Link } from "react-router-dom";

const POSRFIDAnalyticsPage = () => {
  const { token } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("week");
  const [activeTab, setActiveTab] = useState("dashboard");
  
  // Dashboard data
  const [dashboardData, setDashboardData] = useState(null);
  const [conversionData, setConversionData] = useState(null);
  const [dwellData, setDwellData] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  
  // Zones
  const [zones, setZones] = useState([]);
  const [showZoneModal, setShowZoneModal] = useState(false);
  const [editingZone, setEditingZone] = useState(null);
  const [zoneForm, setZoneForm] = useState({
    name: "",
    zone_type: "display",
    description: ""
  });
  
  useEffect(() => {
    fetchData();
  }, [period]);
  
  useEffect(() => {
    fetchZones();
    fetchRecommendations();
  }, []);
  
  const fetchData = async () => {
    setLoading(true);
    try {
      const [dashboardRes, conversionRes, dwellRes] = await Promise.all([
        fetch(`${API}/pos/rfid/analytics/dashboard?period=${period}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API}/pos/rfid/analytics/conversion?period=${period}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API}/pos/rfid/analytics/dwell-time?period=${period}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      if (dashboardRes.ok) setDashboardData(await dashboardRes.json());
      if (conversionRes.ok) setConversionData(await conversionRes.json());
      if (dwellRes.ok) setDwellData(await dwellRes.json());
    } catch (err) {
      console.error("Error fetching RFID data:", err);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchZones = async () => {
    try {
      const res = await fetch(`${API}/pos/rfid/zones`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setZones(await res.json());
      }
    } catch (err) {
      console.error("Error fetching zones:", err);
    }
  };
  
  const fetchRecommendations = async () => {
    try {
      const res = await fetch(`${API}/pos/rfid/analytics/recommendations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setRecommendations(data.recommendations || []);
      }
    } catch (err) {
      console.error("Error fetching recommendations:", err);
    }
  };
  
  const handleCreateZone = async () => {
    try {
      const res = await fetch(`${API}/pos/rfid/zones`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(zoneForm)
      });
      
      if (res.ok) {
        toast.success("Zone created successfully");
        setShowZoneModal(false);
        setZoneForm({ name: "", zone_type: "display", description: "" });
        fetchZones();
      } else {
        toast.error("Failed to create zone");
      }
    } catch (err) {
      toast.error("Error creating zone");
    }
  };
  
  const handleUpdateZone = async () => {
    try {
      const res = await fetch(`${API}/pos/rfid/zones/${editingZone.zone_id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(zoneForm)
      });
      
      if (res.ok) {
        toast.success("Zone updated successfully");
        setShowZoneModal(false);
        setEditingZone(null);
        setZoneForm({ name: "", zone_type: "display", description: "" });
        fetchZones();
      } else {
        toast.error("Failed to update zone");
      }
    } catch (err) {
      toast.error("Error updating zone");
    }
  };
  
  const handleDeleteZone = async (zoneId) => {
    if (!confirm("Are you sure you want to delete this zone?")) return;
    
    try {
      const res = await fetch(`${API}/pos/rfid/zones/${zoneId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success("Zone deleted");
        fetchZones();
      } else {
        toast.error("Failed to delete zone");
      }
    } catch (err) {
      toast.error("Error deleting zone");
    }
  };
  
  const openEditZone = (zone) => {
    setEditingZone(zone);
    setZoneForm({
      name: zone.name,
      zone_type: zone.zone_type,
      description: zone.description || ""
    });
    setShowZoneModal(true);
  };
  
  const formatTime = (seconds) => {
    if (!seconds) return "0s";
    if (seconds < 60) return `${Math.round(seconds)}s`;
    return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
  };
  
  const getZoneTypeColor = (type) => {
    const colors = {
      entrance: "bg-blue-500/20 text-blue-400 border-blue-500/30",
      aisle: "bg-slate-500/20 text-gray-500 border-slate-500/30",
      display: "bg-indigo-500/20 text-indigo-400 border-indigo-500/30",
      checkout: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
      fitting_room: "bg-purple-500/20 text-purple-400 border-purple-500/30"
    };
    return colors[type] || colors.aisle;
  };
  
  const getPriorityColor = (priority) => {
    const colors = {
      high: "bg-red-500/20 text-red-400 border-red-500/30",
      opportunity: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
      medium: "bg-amber-500/20 text-amber-400 border-amber-500/30",
      low: "bg-slate-500/20 text-gray-500 border-slate-500/30"
    };
    return colors[priority] || colors.medium;
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white text-gray-900">
      {/* Header */}
      <div className="border-b border-gray-200 bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-500 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/merchant/pos" className="text-white/80 hover:text-white">
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2 text-white">
                <Radio className="h-6 w-6 text-fuchsia-200" />
                Store Journey Analytics
              </h1>
              <p className="text-white/70 text-sm">
                RFID-powered customer tracking and zone analytics
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-32 bg-white/20 border-white/30 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="week">This Week</SelectItem>
                <SelectItem value="month">This Month</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
      
      {/* Tabs */}
      <div className="border-b border-gray-200 bg-white/30">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex gap-1">
            {[
              { id: "dashboard", label: "Dashboard", icon: BarChart3 },
              { id: "zones", label: "Zone Management", icon: MapPin },
              { id: "conversion", label: "Conversion", icon: Target },
              { id: "recommendations", label: "Insights", icon: Lightbulb },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-3 text-sm flex items-center gap-2 border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? "border-indigo-500 text-gray-900"
                    : "border-transparent text-gray-500 hover:text-gray-900"
                }`}
              >
                <tab.icon className="h-4 w-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="max-w-7xl mx-auto p-6">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500"></div>
          </div>
        ) : (
          <>
            {/* Dashboard Tab */}
            {activeTab === "dashboard" && dashboardData && (
              <div className="space-y-6">
                {/* Summary Cards */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 bg-indigo-500/20 rounded-lg">
                        <Activity className="h-5 w-5 text-indigo-400" />
                      </div>
                      <span className="text-gray-500 text-sm">Total Events</span>
                    </div>
                    <p className="text-3xl font-bold">{dashboardData.summary.total_events.toLocaleString()}</p>
                  </div>
                  
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 bg-emerald-500/20 rounded-lg">
                        <Users className="h-5 w-5 text-emerald-400" />
                      </div>
                      <span className="text-gray-500 text-sm">Unique Visitors</span>
                    </div>
                    <p className="text-3xl font-bold">{dashboardData.summary.unique_visitors.toLocaleString()}</p>
                  </div>
                  
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 bg-amber-500/20 rounded-lg">
                        <Clock className="h-5 w-5 text-amber-400" />
                      </div>
                      <span className="text-gray-500 text-sm">Avg Dwell Time</span>
                    </div>
                    <p className="text-3xl font-bold">{formatTime(dashboardData.summary.avg_dwell_time)}</p>
                  </div>
                </div>
                
                {/* Zone Traffic */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-indigo-400" />
                    Zone Traffic
                  </h3>
                  
                  {dashboardData.zone_traffic.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">
                      No RFID data yet. Set up zones and start ingesting events.
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {dashboardData.zone_traffic.map(zone => (
                        <div key={zone.zone_id} className="flex items-center gap-4 p-3 bg-gray-100/50 rounded-lg">
                          <div className={`px-2 py-1 rounded text-xs border ${getZoneTypeColor(zone.zone_type)}`}>
                            {zone.zone_type}
                          </div>
                          <div className="flex-1">
                            <p className="font-medium">{zone.zone_name}</p>
                            <p className="text-xs text-gray-500">
                              {zone.unique_visitors} visitors • {zone.interactions} interactions
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold">{zone.total_events}</p>
                            <p className="text-xs text-gray-500">events</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-amber-400">{formatTime(zone.avg_dwell_time)}</p>
                            <p className="text-xs text-gray-500">avg dwell</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                {/* Hourly Distribution */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <BarChart3 className="h-5 w-5 text-indigo-400" />
                    Hourly Traffic Distribution
                  </h3>
                  <div className="flex items-end gap-1 h-32">
                    {dashboardData.hourly_distribution.map((h, idx) => {
                      const maxCount = Math.max(...dashboardData.hourly_distribution.map(x => x.count), 1);
                      const height = (h.count / maxCount) * 100;
                      return (
                        <div key={idx} className="flex-1 flex flex-col items-center">
                          <div
                            className="w-full bg-indigo-500/50 rounded-t"
                            style={{ height: `${height}%`, minHeight: h.count > 0 ? "4px" : "0" }}
                          ></div>
                          {idx % 3 === 0 && (
                            <span className="text-xs text-gray-500 mt-1">{h.hour}:00</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
            
            {/* Zones Tab */}
            {activeTab === "zones" && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h2 className="text-lg font-semibold">Store Zones</h2>
                  <Button
                    onClick={() => {
                      setEditingZone(null);
                      setZoneForm({ name: "", zone_type: "display", description: "" });
                      setShowZoneModal(true);
                    }}
                    className="bg-indigo-600 hover:bg-indigo-700"
                    data-testid="add-zone-btn"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Zone
                  </Button>
                </div>
                
                {zones.length === 0 ? (
                  <div className="text-center py-12 bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-500 rounded-xl border border-gray-200">
                    <MapPin className="h-12 w-12 mx-auto text-slate-600 mb-4" />
                    <p className="text-gray-500 mb-4">No zones configured yet.</p>
                    <Button
                      onClick={() => setShowZoneModal(true)}
                      variant="outline"
                      className="border-gray-200"
                    >
                      Create Your First Zone
                    </Button>
                  </div>
                ) : (
                  <div className="grid gap-4">
                    {zones.map(zone => (
                      <div key={zone.zone_id} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`px-3 py-1.5 rounded border ${getZoneTypeColor(zone.zone_type)}`}>
                            {zone.zone_type.replace("_", " ")}
                          </div>
                          <div>
                            <p className="font-medium">{zone.name}</p>
                            {zone.description && (
                              <p className="text-sm text-gray-500">{zone.description}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditZone(zone)}
                            className="text-gray-500 hover:text-gray-900"
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteZone(zone.zone_id)}
                            className="text-gray-500 hover:text-red-400"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            {/* Conversion Tab */}
            {activeTab === "conversion" && conversionData && (
              <div className="space-y-6">
                {/* Summary */}
                <div className="grid grid-cols-4 gap-4">
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
                    <p className="text-gray-500 text-sm mb-1">Total Interactions</p>
                    <p className="text-2xl font-bold">{conversionData.summary.total_interactions.toLocaleString()}</p>
                  </div>
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
                    <p className="text-gray-500 text-sm mb-1">Unique Visitors</p>
                    <p className="text-2xl font-bold">{conversionData.summary.unique_visitors.toLocaleString()}</p>
                  </div>
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
                    <p className="text-gray-500 text-sm mb-1">Total Purchases</p>
                    <p className="text-2xl font-bold">{conversionData.summary.total_purchases.toLocaleString()}</p>
                  </div>
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
                    <p className="text-gray-500 text-sm mb-1">Conversion Rate</p>
                    <p className="text-2xl font-bold text-emerald-400">{conversionData.summary.overall_conversion_rate}%</p>
                  </div>
                </div>
                
                {/* Product Conversion Table */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <Target className="h-5 w-5 text-indigo-400" />
                    Product Conversion Analysis
                  </h3>
                  
                  {conversionData.products.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">
                      No conversion data available. Start tracking product interactions.
                    </p>
                  ) : (
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-gray-500 border-b border-gray-200">
                          <th className="text-left py-2">Product</th>
                          <th className="text-right py-2">Interactions</th>
                          <th className="text-right py-2">Visitors</th>
                          <th className="text-right py-2">Purchased</th>
                          <th className="text-right py-2">Conversion</th>
                        </tr>
                      </thead>
                      <tbody>
                        {conversionData.products.map(p => (
                          <tr key={p.product_id} className="border-b border-white/5">
                            <td className="py-3">
                              <p className="font-medium">{p.name}</p>
                              {p.sku && <p className="text-xs text-gray-500">{p.sku}</p>}
                            </td>
                            <td className="text-right">{p.interactions}</td>
                            <td className="text-right">{p.unique_visitors}</td>
                            <td className="text-right">{p.quantity_sold}</td>
                            <td className="text-right">
                              <span className={p.conversion_rate > 50 ? "text-emerald-400" : p.conversion_rate > 20 ? "text-amber-400" : "text-gray-500"}>
                                {p.conversion_rate}%
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            )}
            
            {/* Recommendations Tab */}
            {activeTab === "recommendations" && (
              <div className="space-y-4">
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <Lightbulb className="h-5 w-5 text-amber-400" />
                  AI-Powered Insights
                </h2>
                
                {recommendations.length === 0 ? (
                  <div className="text-center py-12 bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-500 rounded-xl border border-gray-200">
                    <Lightbulb className="h-12 w-12 mx-auto text-slate-600 mb-4" />
                    <p className="text-gray-500">
                      No recommendations yet. Collect more RFID data to get personalized insights.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {recommendations.map((rec, idx) => (
                      <div key={idx} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 flex items-start gap-4">
                        <div className={`px-2 py-1 rounded text-xs border ${getPriorityColor(rec.priority)}`}>
                          {rec.priority}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium mb-1">{rec.zone_name}</p>
                          <p className="text-sm text-gray-500">{rec.message}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
      
      {/* Zone Modal */}
      <Dialog open={showZoneModal} onOpenChange={setShowZoneModal}>
        <DialogContent className="bg-white border-gray-200">
          <DialogHeader>
            <DialogTitle>{editingZone ? "Edit Zone" : "Create Zone"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-gray-500 mb-1 block">Zone Name</label>
              <Input
                value={zoneForm.name}
                onChange={(e) => setZoneForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Main Entrance"
                className="bg-gray-100 border-gray-200"
                data-testid="zone-name-input"
              />
            </div>
            <div>
              <label className="text-sm text-gray-500 mb-1 block">Zone Type</label>
              <Select value={zoneForm.zone_type} onValueChange={(v) => setZoneForm(prev => ({ ...prev, zone_type: v }))}>
                <SelectTrigger className="bg-gray-100 border-gray-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="entrance">Entrance</SelectItem>
                  <SelectItem value="aisle">Aisle</SelectItem>
                  <SelectItem value="display">Display</SelectItem>
                  <SelectItem value="checkout">Checkout</SelectItem>
                  <SelectItem value="fitting_room">Fitting Room</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm text-gray-500 mb-1 block">Description (Optional)</label>
              <Input
                value={zoneForm.description}
                onChange={(e) => setZoneForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Brief description of this zone"
                className="bg-gray-100 border-gray-200"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowZoneModal(false)} className="border-gray-200">
              Cancel
            </Button>
            <Button
              onClick={editingZone ? handleUpdateZone : handleCreateZone}
              disabled={!zoneForm.name}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="save-zone-btn"
            >
              {editingZone ? "Update Zone" : "Create Zone"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default POSRFIDAnalyticsPage;
